import { useCallback, useEffect, useState } from "react";
import { ClipboardList, Plus, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { inr } from "@/lib/format";
import * as inventoryApi from "@/lib/inventoryApi";

const errorText = err => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || err?.message || "The server did not answer";
};
const today = () => new Date().toLocaleDateString("en-CA");
const qty = v => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 }));

/**
 * Physical counts (owner brief 2026-11-01 §3.3): one store on one day. Every item
 * of the store is listed with an EMPTY box — nothing is pre-filled with the books'
 * figure, and a box left blank is "not counted", never zero. Posting writes the
 * differences to the ledger; the variance shows them by rupee size.
 */
export default function StockCounts({ token, stores, canWrite }) {
  const [list, setList] = useState({ phase: "loading", rows: [] });
  const [open, setOpen] = useState(null);         // the count being worked on (variance shape)
  const [entries, setEntries] = useState({});     // stock_item_id -> typed text
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ department_id: "", effective_date: today(), counted_by: "" });
  const storeName = id => stores.find(s => s.id === id)?.name || "All stores";

  const load = useCallback(() => {
    inventoryApi.listCounts(null, token)
      .then(rows => setList({ phase: "ready", rows }))
      .catch(err => setList({ phase: "error", rows: [], reason: errorText(err) }));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const show = count => {
    setOpen(count);
    const typed = {};
    for (const line of count.lines || []) typed[line.stock_item_id] = String(line.counted_qty);
    setEntries(typed);
  };

  const start = async () => {
    setBusy(true);
    try {
      const count = await inventoryApi.createCount({
        department_id: draft.department_id ? Number(draft.department_id) : null,
        effective_date: draft.effective_date || null,
        counted_by: draft.counted_by.trim() || null,
      }, token);
      show(count);
      load();
    } catch (err) {
      toast.error(`Count not started — ${errorText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const allItems = open ? [...open.lines, ...open.not_counted].sort((a, b) => a.name.localeCompare(b.name)) : [];

  const save = async (andPost) => {
    const lines = allItems.map(it => {
      const raw = (entries[it.stock_item_id] ?? "").trim();
      return { stock_item_id: it.stock_item_id, counted_qty: raw === "" ? null : Number(raw) };
    });
    if (lines.some(l => l.counted_qty !== null && (!Number.isFinite(l.counted_qty) || l.counted_qty < 0))) {
      return toast.error("A counted quantity must be a number, 0 or more — leave it blank if not counted");
    }
    setBusy(true);
    try {
      let count = await inventoryApi.setCountLines(open.id, lines, token);
      if (andPost) {
        count = await inventoryApi.postCount(open.id, token);
        toast.success(`Count posted · ${count.counted_items} of ${count.total_items} counted`);
      } else {
        toast.success("Count saved — not posted yet");
      }
      show(count);
      load();
    } catch (err) {
      toast.error(`${andPost ? "Not posted" : "Not saved"} — ${errorText(err)}`);
    } finally {
      setBusy(false);
    }
  };

  if (open) {
    const posted = open.status === "posted";
    return (
      <div className="space-y-base" data-testid="stock-count-sheet">
        <div className="flex flex-wrap items-baseline justify-between gap-snug">
          <div>
            <h3 className="text-title font-semibold">{storeName(open.department_id)} · {open.effective_date}</h3>
            <p className="text-meta text-muted-foreground" data-testid="stock-count-summary">
              {posted ? "Posted" : "Draft"} · {open.counted_items} of {open.total_items} counted
              {open.difference_value !== null && posted ? ` · difference ${inr(open.difference_value)}` : ""}
            </p>
          </div>
          <Button variant="outline" className="h-11" onClick={() => setOpen(null)} data-testid="stock-count-back">All counts</Button>
        </div>
        {!posted && (
          <p className="text-meta text-muted-foreground">Enter what is on the shelf. Leave a box empty if you did not count it — it stays "not counted", never zero.</p>
        )}
        <div className="border rounded-lg divide-y">
          <div className="grid grid-cols-12 gap-snug px-base py-snug text-micro font-medium uppercase tracking-wide text-muted-foreground">
            <span className="col-span-5">Item</span>
            <span className="col-span-3">Counted</span>
            {posted && <span className="col-span-2 text-right">Books</span>}
            {posted && <span className="col-span-2 text-right">Difference</span>}
          </div>
          {(posted ? open.lines : allItems).map(it => {
            const line = open.lines.find(l => l.stock_item_id === it.stock_item_id);
            return (
              <div key={it.stock_item_id} className="grid grid-cols-12 gap-snug items-center px-base py-snug" data-testid={`count-line-${it.stock_item_id}`}>
                <span className="col-span-5 text-body truncate">{it.name} <span className="text-micro text-muted-foreground">{it.unit}</span></span>
                <div className="col-span-3">
                  {posted ? <span className="tabular-nums">{qty(line?.counted_qty)}</span> : (
                    <Input className="h-11" type="number" inputMode="decimal" min="0" step="0.001" placeholder="not counted"
                           value={entries[it.stock_item_id] ?? ""} disabled={!canWrite}
                           onChange={e => setEntries(m => ({ ...m, [it.stock_item_id]: e.target.value }))}
                           data-testid={`count-line-${it.stock_item_id}-input`} />
                  )}
                </div>
                {posted && <span className="col-span-2 text-right tabular-nums">{qty(line?.system_qty)}</span>}
                {posted && (
                  <span className={`col-span-2 text-right tabular-nums ${line?.difference < 0 ? "text-destructive" : ""}`}>
                    {qty(line?.difference)}{line?.difference_value !== null && line?.difference_value !== undefined ? ` · ${inr(line.difference_value)}` : ""}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {posted && open.not_counted.length > 0 && (
          <p className="text-meta text-muted-foreground" data-testid="stock-count-not-counted">
            Not counted ({open.not_counted.length}): {open.not_counted.map(n => n.name).join(", ")}
          </p>
        )}
        {!posted && canWrite && (
          <div className="grid grid-cols-2 gap-snug">
            <Button variant="outline" className="h-11" disabled={busy} onClick={() => save(false)} data-testid="stock-count-save">Save, post later</Button>
            <Button variant="action" className="h-11" disabled={busy} onClick={() => save(true)} data-testid="stock-count-post">Post the count</Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-base" data-testid="stock-counts-panel">
      {canWrite && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-base items-end bg-muted/30 p-base rounded-lg border" data-testid="stock-count-new">
          <label className="space-y-tight text-meta font-medium text-muted-foreground">Store
            <select className="w-full h-11 px-snug border rounded-md bg-card text-body" value={draft.department_id}
                    onChange={e => setDraft(d => ({ ...d, department_id: e.target.value }))} data-testid="stock-count-store">
              <option value="">Every store</option>
              {stores.filter(s => s.is_active !== false).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <div className="space-y-tight text-meta font-medium text-muted-foreground">Count date
            <DatePicker value={draft.effective_date} onChange={v => setDraft(d => ({ ...d, effective_date: v }))} ariaLabel="Count date" testId="stock-count-date" />
          </div>
          <label className="space-y-tight text-meta font-medium text-muted-foreground">Counted by
            <Input className="h-11" value={draft.counted_by} onChange={e => setDraft(d => ({ ...d, counted_by: e.target.value }))} data-testid="stock-count-by" />
          </label>
          <Button variant="action" className="h-11" disabled={busy} onClick={start} data-testid="stock-count-start">
            <Plus className="w-4 h-4 mr-tight" />Start a count
          </Button>
        </div>
      )}
      {list.phase === "error" && (
        <p className="text-meta text-destructive flex items-center gap-tight" role="alert"><TriangleAlert className="w-4 h-4" />Counts not read: {list.reason}</p>
      )}
      {list.phase === "ready" && list.rows.length === 0 && (
        <p className="text-meta text-muted-foreground flex items-center gap-tight" data-testid="stock-counts-empty"><ClipboardList className="w-4 h-4" />No counts yet.</p>
      )}
      <ul className="border rounded-lg divide-y" data-testid="stock-counts-list">
        {list.rows.map(c => (
          <li key={c.id}>
            <button className="w-full flex items-center justify-between gap-snug px-base py-snug min-h-11 text-left hover:bg-muted"
                    onClick={() => inventoryApi.getCount(c.id, token).then(show).catch(err => toast.error(errorText(err)))}
                    data-testid={`stock-count-${c.id}`}>
              <span className="text-body font-medium">{storeName(c.department_id)} · {c.effective_date}</span>
              <span className={`text-meta ${c.status === "posted" ? "text-muted-foreground" : "text-warning"}`}>{c.status === "posted" ? "Posted" : "Draft"}{c.counted_by ? ` · ${c.counted_by}` : ""}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
